import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getOutfits, deleteOutfit, toggleOutfitVisibility, searchOutfits, Outfit } from '@/services/outfitsService';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, Trash2, Search, ImageIcon, Loader2 } from 'lucide-react';

const OutfitsList = () => {
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<string>('all');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  const fetchOutfits = async () => {
    try {
      setLoading(true);
      if (searchQuery.trim()) {
        const data = await searchOutfits(searchQuery);
        setOutfits(data);
      } else {
        const { data } = await getOutfits(1, 100);
        setOutfits(data);
      }
    } catch (error: any) {
      console.error('Error fetching outfits:', error);
      toast.error('Failed to load outfits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOutfits();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchOutfits();
  };

  const handleDelete = async (id: string) => {
    try {
      setDeleting(id);
      await deleteOutfit(id);
      setOutfits(prev => prev.filter(o => o.id !== id));
      toast.success('Outfit deleted successfully');
    } catch (error: any) {
      console.error('Error deleting outfit:', error);
      toast.error('Failed to delete outfit');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleVisibility = async (id: string, current: boolean) => {
    try {
      setToggling(id);
      await toggleOutfitVisibility(id, !current);
      setOutfits(prev => prev.map(o => o.id === id ? { ...o, is_public: !current } : o));
      toast.success(`Outfit ${current ? 'hidden' : 'published'}`);
    } catch (error: any) {
      console.error('Error toggling visibility:', error);
      toast.error('Failed to update visibility');
    } finally {
      setToggling(null);
    }
  };

  const filteredOutfits = visibilityFilter === 'all'
    ? outfits
    : outfits.filter(o => visibilityFilter === 'public' ? o.is_public : !o.is_public);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Outfits</h1>
          <p className="text-muted-foreground">Manage all user outfits</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search outfits by title, description, or user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button type="submit" variant="secondary">Search</Button>
        </form>
        <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All outfits" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outfits</SelectItem>
            <SelectItem value="public">Public only</SelectItem>
            <SelectItem value="private">Private only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-background border border-border/50 shadow-subtle">
        <CardHeader className="pb-2">
          <CardTitle>All Outfits</CardTitle>
          <CardDescription>
            {loading ? 'Loading...' : `${filteredOutfits.length} outfit(s)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                <span className="text-sm text-muted-foreground">Loading outfits...</span>
              </div>
            </div>
          ) : filteredOutfits.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium">No outfits found</h3>
                <p className="text-muted-foreground">
                  {searchQuery ? 'Try a different search term' : 'No outfits have been created yet'}
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Image</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Views</TableHead>
                    <TableHead>Likes</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOutfits.map((outfit) => (
                    <TableRow key={outfit.id}>
                      <TableCell>
                        <div className="w-10 h-10 rounded-md bg-muted overflow-hidden">
                          {outfit.image_url ? (
                            <img
                              src={outfit.image_url}
                              alt={outfit.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/placeholder.svg';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImageIcon className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Link to={`/admin/outfits/${outfit.id}`} className="font-medium hover:text-primary transition-colors">
                          {outfit.title}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {outfit.user_email || outfit.user_name || 'Unknown'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {outfit.items?.length || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{outfit.view_count}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{outfit.like_count}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={outfit.is_public ? 'default' : 'secondary'}>
                          {outfit.is_public ? 'Public' : 'Private'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {new Date(outfit.created_at).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleToggleVisibility(outfit.id, outfit.is_public)}
                            disabled={toggling === outfit.id}
                          >
                            {toggling === outfit.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : outfit.is_public ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-background border border-border/50">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Outfit</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete "{outfit.title}" and all its items. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-background hover:bg-muted">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-white hover:bg-destructive/90"
                                  onClick={() => handleDelete(outfit.id)}
                                  disabled={deleting === outfit.id}
                                >
                                  {deleting === outfit.id ? 'Deleting...' : 'Delete'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OutfitsList;
